/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { get } from 'lodash';
import { RRule, Weekday } from 'rrule';
import { format, parseISO } from 'date-fns';
import { PeriodOptions, StopRepeatingOptions, DATE_FORMAT, PERIODS } from './utils';

/**
 * Enum that maps PeriodOptions to RRules frequences
 * @readonly
 * @enum {RRule} RRule.WEEKLY | RRule.MONTHLY | RRule.YEARLY
 */
export enum RuleTypes {
  /** eg.: on Friday */
  'Day' = 'DAY',
  /** eg.: on the 12th */
  'Date' = 'DATE',
  /** eg.: on the last day */
  'LastDay' = 'LAST_DAY',
  /** eg.: on the 3rd (or last) Friday */
  'NWeekDay' = 'N_WEEK_DAY',
  /** eg.: on the last workday */
  'LastWorkDay' = 'LAST_WORK_DAY',
}

/** @constant
 * Constant that maps PeriodOptions to RRule frequences and intervals
 * @type {object}
 * @default
 * [PeriodOptions.Weekly]: freq: RRule.WEEKLY
 * [PeriodOptions.Monthly]:freq: RRule.MONTHLY
 * [PeriodOptions.Quarterly]: freq: RRule.MONTHLY - interval: 3
 * [PeriodOptions.Semiannually]: freq: RRule.MONTHLY - interval: 6
 * [PeriodOptions.Yearly] - freq: RRule.YEARLY
 * [PeriodOptions.Annually] - freq: RRule.YEARLY
 */
export const RULES_MAP = {
  /** freq: RRule.WEEKLY */
  [PeriodOptions.Weekly]: {
    freq: RRule.WEEKLY,
  },
  /** freq: RRule.MONTHLY */
  [PeriodOptions.Monthly]: {
    freq: RRule.MONTHLY,
  },
  /** freq: RRule.MONTHLY - interval: 3 */
  [PeriodOptions.Quarterly]: {
    freq: RRule.MONTHLY,
    interval: 3,
  },
  /** freq: RRule.MONTHLY - interval: 6 */
  [PeriodOptions.Semiannually]: {
    freq: RRule.MONTHLY,
    interval: 6,
  },
  /** freq: RRule.YEARLY */
  [PeriodOptions.Yearly]: {
    freq: RRule.YEARLY,
  },
  /** freq: RRule.YEARLY */
  [PeriodOptions.Annually]: {
    freq: RRule.YEARLY,
  },
};

/** @constant
 * Constant that maps RRule frequences to zen Periods
 * @type {object}
 * @default
 * [RRule.WEEKLY]: period: PeriodOptions.Weekly
 * [RRule.MONTHLY]: period: PeriodOptions.Monthly
 * [RRule.YEARLY]: period: PeriodOptions.Yearly
 */
export const RULES_PERIODS = {
  /** freq: RRule.WEEKLY */
  [RRule.WEEKLY]: PERIODS[0],
  /** freq: RRule.MONTHLY */
  [RRule.MONTHLY]: PERIODS[1],
  /** freq: RRule.YEARLY */
  [RRule.YEARLY]: PERIODS[2],
};

/**
 * Interface for classes that represents a RRule rule.
 * @interface
 */
export interface Rule {
  freq: string;
  interval?: number;
  count?: number;
  until?: Date;
  bysetpos?: number;
  bymonth?: any;
  bymonthday?: any;
  bynmonthday?: any;
  byyearday?: any;
  byweekno?: any;
  byweekday?: any;
  bynweekday?: any;
  byhour?: any;
  byminute?: any;
  bysecond?: any;
  byeaster?: any;
}

/**
 * Returns an object representing an RRule for the specified rule type
 * @param {string} period WEEKLY | MONTHLY | QUARTERLY | SEMIANNUALLY | ANNUALLY/YEARLY
 * @param {RuleTypes} ruleType
 * @param {Object} attributes Contains date attributes which the suggestions are based on
 */
export function getRule(period: string, ruleType: RuleTypes, attributes: any): Rule {
  let rule: Rule = {
    freq: RULES_MAP[period].freq,
  };

  switch (ruleType) {
    case RuleTypes.Day:
      // e.g. due on Friday
      rule.byweekday = [new Weekday(attributes.dayOfTheWeek - 1)];
      break;

    case RuleTypes.Date:
      // e.g. due on the 5th [of the month / of July]
      rule.bymonthday = attributes.dayNumber;
      if (attributes.dayNumber === 29) {
        rule.bymonthday = [28, 29];
        rule.bysetpos = -1;
      }
      break;

    case RuleTypes.LastDay:
      // e.g. due on the last day of [the month / July]
      rule.bymonthday = -1;
      break;

    case RuleTypes.NWeekDay:
      // e.g. due on the last Friday of [the month / July] --> weekNumber: -1
      // e.g. due on the 3rd Friday of [the month / July] --> weekNumber: 3
      rule.byweekday = [attributes.weekday.nth(attributes.weekNumber)];
      break;

    case RuleTypes.LastWorkDay:
      // e.g. due on the last weekday of [the month / July]
      rule.byweekday = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR];
      rule.bysetpos = -1;
      break;

    default:
      break;
  }

  if (period === PeriodOptions.Yearly || period === PeriodOptions.Annually) {
    rule.bymonth = attributes.byMonth;
  }

  if (period === PeriodOptions.Quarterly || period === PeriodOptions.Semiannually) {
    rule.interval = RULES_MAP[period].interval;
  }

  return rule;
}

/**
 * Returns a string that is created based on the RRule created
 * @param {object} form Contains all recurrence form data
 */
export function createStringRule(form) {
  let ruleObj = {};

  // 1. Check if a quick suggestions was selected
  let repeatOptionRule = get(form, 'repeatOption.rule');
  if (repeatOptionRule) {
    ruleObj = {
      ...repeatOptionRule,
    };
  }

  // 2. Check if the user selected a custom repeat option
  let repeatOnRule = get(form, 'repeatOn.rule');
  if (repeatOnRule) {
    ruleObj = {
      ...repeatOnRule,
    };
    let interval = get(form, 'repeatInterval', 1);
    if (interval > 1) ruleObj.interval = interval;
  }

  // 3. Check if the user selected a stop recurrence option: date or x ocurrences
  let stopRepeatingValue = get(form, 'stopRepeating.value');
  if (stopRepeatingValue === StopRepeatingOptions.On && form.stopOn) {
    const splittedDate = form.stopOn.split('-');
    ruleObj.until = new Date(Date.UTC(splittedDate[0], parseInt(splittedDate[1]) - 1, splittedDate[2], 0, 0, 0));
  }

  if (stopRepeatingValue === StopRepeatingOptions.After && form.repeatTimes) {
    ruleObj.count = form.repeatTimes;
  }

  const rule = new RRule(ruleObj);
  return rule.toString();
}

export function parseRule(rule: string) {
  const parsedRule = RRule.fromString(rule);
  if (rule.includes('UNTIL')) {
    const splittedRule = rule.split(';');
    const until = splittedRule.find(r => r.includes('UNTIL'));
    if (until) {
      const stopOn = until.split('=')[1];
      parsedRule.options.untilString = format(parseISO(stopOn.substr(0, stopOn.indexOf('T'))), DATE_FORMAT);
    }
  }

  return parsedRule;
}
